/**
 * 资源配置 - 管理 3D 模型和静态资源的加载来源
 *
 * 支持两种模式：
 * 1. 本地开发：从 src/assets 加载
 * 2. 生产环境：从 Cloudflare R2 CDN 加载
 *
 * 环境变量配置（在 .env.local 或 .env.production 中设置）：
 *
 * VITE_R2_BASE_URL - R2 公开访问 URL
 *   示例: https://pub-xxxxx.r2.dev 或 https://assets.yourdomain.com
 *
 * VITE_USE_CDN - 是否强制使用 CDN（可选）
 *   设为 "true" 可在开发环境测试 CDN 加载
 *   默认在生产环境自动启用
 *
 * R2 存储桶目录结构：
 *   /models/
 *     - harley_davidson_xr1200x.glb
 *     - harley-davidson_police.glb
 *   /textures/
 *     - flame_pattern.png
 *     - horse_pattern.png
 *
 * R2 CORS 配置（在 Cloudflare R2 控制台设置）：
 *   [
 *     {
 *       "AllowedOrigins": ["*"],
 *       "AllowedMethods": ["GET", "HEAD"],
 *       "AllowedHeaders": ["*"],
 *       "MaxAgeSeconds": 86400
 *     }
 *   ]
 */

// R2 CDN 基础 URL（从环境变量读取）
const R2_BASE_URL = import.meta.env.VITE_R2_BASE_URL || '';

// 是否使用 CDN（生产环境或明确配置时使用）
const USE_CDN = import.meta.env.VITE_USE_CDN === 'true' ||
                (import.meta.env.PROD && R2_BASE_URL);

/**
 * 3D 模型资源配置
 */
export const models = {
  // 哈雷摩托车模型
  harleyXR1200X: USE_CDN
    ? `${R2_BASE_URL}/models/harley_davidson_xr1200x.glb`
    : null, // null 表示使用本地 import

  harleyPolice: USE_CDN
    ? `${R2_BASE_URL}/models/harley-davidson_police.glb`
    : null,
};

/**
 * 纹理资源配置
 */
export const textures = {
  flamePattern: USE_CDN
    ? `${R2_BASE_URL}/textures/flame_pattern.png`
    : null,

  horsePattern: USE_CDN
    ? `${R2_BASE_URL}/textures/horse_pattern.png`
    : null,
};

/**
 * 获取模型 URL
 * @param {string} modelKey - 模型键名
 * @param {string} localImport - 本地 import 的模块（fallback）
 * @returns {string} 模型 URL
 */
export function getModelUrl(modelKey, localImport) {
  if (USE_CDN && models[modelKey]) {
    return models[modelKey];
  }
  return localImport;
}

/**
 * 获取纹理 URL
 * @param {string} textureKey - 纹理键名
 * @param {string} localImport - 本地 import 的模块（fallback）
 * @returns {string} 纹理 URL
 */
export function getTextureUrl(textureKey, localImport) {
  if (USE_CDN && textures[textureKey]) {
    return textures[textureKey];
  }
  return localImport;
}

/**
 * 检查是否使用 CDN
 */
export function isUsingCDN() {
  return USE_CDN;
}

/**
 * 获取 R2 基础 URL
 */
export function getR2BaseUrl() {
  return R2_BASE_URL;
}

export default {
  models,
  textures,
  getModelUrl,
  getTextureUrl,
  isUsingCDN,
  getR2BaseUrl,
};
